import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Field, TextInput } from "@/components/FormKit";
import { visitsApi, apiErrorMessage } from "@/api/client";
import { toast } from "sonner";

// Phase FU-1 — the 9 locked structured outcome codes that drive deterministic follow-ups.
const OUTCOMES = [
  "met_dm", "gatekeeper_only", "no_show", "not_interested_now", "closed_won",
  "closed_lost", "competitor_in_place", "site_closed", "wrong_fit",
];
const labelize = (s) => s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

/** Fast, thumb-friendly outcome capture: big outcome buttons + one notes field. */
export function VisitOutcomeDialog({ open, onOpenChange, visit, onSaved }) {
  const [outcome, setOutcome] = useState("");
  const [whatHappened, setWhatHappened] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) { setOutcome(""); setWhatHappened(""); setError(""); }
  }, [open]);

  const submit = async () => {
    if (!outcome) { setError("Pick an outcome"); return; }
    setSaving(true); setError("");
    try {
      const res = await visitsApi.outcome(visit.id, { outcome_code: outcome, notes: whatHappened || null });
      toast.success("Outcome recorded");
      onSaved?.(res.data);
      onOpenChange(false);
    } catch (err) { setError(apiErrorMessage(err)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-lg" data-testid="visit-outcome-dialog">
        <DialogHeader><DialogTitle className="font-heading">Record outcome</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-2">
            {OUTCOMES.map((o) => (
              <button key={o} type="button" data-testid={`outcome-${o}`} onClick={() => setOutcome(o)}
                className={`min-h-[52px] rounded-lg border px-3 text-sm font-semibold transition-colors ${
                  outcome === o ? "border-teal-700 bg-teal-700 text-white" : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                }`}>
                {labelize(o)}
              </button>
            ))}
          </div>
          <Field label="Notes (optional)">
            <TextInput testid="outcome-notes" value={whatHappened} onChange={(e) => setWhatHappened(e.target.value)} />
          </Field>
          {error && <p data-testid="visit-outcome-error" className="text-sm font-medium text-rose-600">{error}</p>}
          <button type="button" onClick={submit} disabled={saving} data-testid="visit-outcome-submit"
            className="min-h-[52px] w-full rounded-lg bg-teal-700 font-semibold text-white hover:bg-teal-800 disabled:opacity-60">
            {saving ? "Saving…" : "Submit outcome"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
